/** Macro #30 — Public creator profile surface (`#u/<wallet>`). */
import { useQuery } from "@tanstack/react-query";
import { cn } from "../cn";
import { socialApi, creatorLabel, shortWallet } from "../../lib/social-api";
import { useSession } from "../marketplace/useSession";
import { FollowButton } from "./FollowButton";
import { HandleSettings } from "./HandleSettings";
import { ReportMenu } from "./ReportMenu";
import { useI18n } from "../../i18n";

function Stat({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div className="flex flex-col">
      <span data-testid={testId} className="font-display text-[20px] font-semibold text-white">{value}</span>
      <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-white/45">{label}</span>
    </div>
  );
}

export function CreatorProfileRoute({ wallet }: { wallet: string | null }) {
  const { t } = useI18n();
  const { wallet: sessionWallet } = useSession();
  // `#u/me` is a convenience alias for the signed-in wallet (resolved client-side;
  // the backend treats a non-0x token as a handle lookup).
  const resolved = wallet === "me" ? sessionWallet : wallet;
  const isSelf = !!resolved && !!sessionWallet && resolved.toLowerCase() === sessionWallet.toLowerCase();

  const q = useQuery({
    queryKey: ["social", "profile", resolved],
    queryFn: ({ signal }) => socialApi.publicProfile(resolved as string, signal),
    enabled: !!resolved,
    retry: false,
  });

  if (wallet === "me" && !sessionWallet) {
    return (
      <div data-testid="creator-profile-signin" className="mx-auto max-w-3xl p-8 text-white/60">
        <a href="#auth" className="text-neon-teal no-underline hover:underline">{t("social.creator.signinPre")}</a> {t("social.creator.signinPost")}
      </div>
    );
  }
  if (!resolved) {
    return <div data-testid="creator-profile-empty" className="mx-auto max-w-3xl p-8 text-white/50">{t("social.creator.none")}</div>;
  }
  if (q.isLoading) {
    return <div data-testid="creator-profile-loading" className="mx-auto max-w-3xl p-8 font-mono text-sm text-white/50">{t("social.creator.loading")}</div>;
  }
  if (q.isError || !q.data) {
    return <div data-testid="creator-profile-error" className="mx-auto max-w-3xl p-8 font-mono text-sm text-state-danger">{t("social.creator.unavailable")}</div>;
  }

  const p = q.data;

  return (
    <div data-testid="creator-profile-root" className="mx-auto max-w-3xl px-5 py-8 space-y-6">
      <div className="rounded-2xl border border-white/10 bg-[rgba(12,16,22,0.7)] p-6">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-white/12 bg-white/[0.04]">
            {p.avatar_url ? (
              <img src={p.avatar_url} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-[22px] text-white/30">
                {(p.handle ?? shortWallet(p.wallet)).slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h1 data-testid="creator-profile-name" className="truncate font-display text-[22px] font-semibold text-white">
                {p.display_name || creatorLabel(p.handle, p.wallet)}
              </h1>
              <span className="rounded-full border border-white/12 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-white/45">{p.status}</span>
            </div>
            <div className="mt-0.5 font-mono text-[12px] text-neon-teal/80">{creatorLabel(p.handle, p.wallet)}</div>
            {p.bio && <p className="mt-2 text-[13px] text-white/70">{p.bio}</p>}
            {p.links.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {p.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer noopener" data-testid={`creator-link-${i}`}
                     className="rounded-full border border-white/12 px-2.5 py-1 font-mono text-[10px] text-white/70 no-underline hover:border-neon-teal/60 hover:text-white">
                    {l.label || l.url}
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isSelf && <FollowButton creatorWallet={p.wallet} />}
            {!isSelf && <ReportMenu targetType="profile" targetId={p.wallet} authorWallet={p.wallet} testId="creator-report-menu" />}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-4 sm:grid-cols-6">
          <Stat label={t("social.creator.followers")} value={p.followers} testId="stat-followers" />
          <Stat label={t("social.creator.following")} value={p.following} testId="stat-following" />
          <Stat label={t("social.creator.likes")} value={p.historical_likes_total} testId="stat-likes" />
          <Stat label={t("social.creator.comments")} value={p.historical_comments_total} testId="stat-comments" />
          <Stat label={t("social.creator.territories")} value={p.territories_owned} testId="stat-territories" />
          <Stat label={t("social.creator.published")} value={p.territories_published} testId="stat-published" />
        </div>
      </div>

      {isSelf && (
        <>
          <HandleSettings />
          <a href="#feed" data-testid="creator-goto-feed"
             className="inline-block font-mono text-[11px] uppercase tracking-[0.14em] text-neon-teal/80 hover:text-neon-teal no-underline">
            {t("social.creator.gotoFeed")}
          </a>
        </>
      )}

      {p.recent_activity.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[rgba(12,16,22,0.5)] p-5">
          <h3 className="mb-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/55">{t("social.creator.recentActivity")}</h3>
          <ul className="space-y-2" data-testid="creator-recent-activity">
            {p.recent_activity.map((a, i) => {
              const type = String(a.type ?? "");
              const deep = String(a.deep_link ?? "");
              const title = String(a.title ?? type);
              return (
                <li key={i}>
                  <a href={deep.startsWith("#") ? deep : `#${deep}`}
                     className={cn("flex items-center justify-between rounded-lg border border-white/[0.06] px-3 py-2",
                       "text-[12px] text-white/75 no-underline hover:bg-white/[0.04]")}>
                    <span className="truncate">{title}</span>
                    <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-white/35">{type}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
